<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto max-w-6xl px-4 py-12 lg:px-8">
      <div class="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
        <section class="rounded-3xl border border-orange-100 bg-white/90 p-8 shadow-sm">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Invi8 portal</p>
          <h1 class="mt-3 text-3xl font-semibold text-ink">Welcome back to your event flow</h1>
          <p class="mt-2 text-sm text-muted">
            Keep track of venue quotes, package drafts, and client responses in one place.
          </p>

          <div class="mt-8 grid gap-4 sm:grid-cols-2">
            <div class="rounded-2xl border border-orange-100 bg-[#FFF3E5] p-4">
              <p class="text-sm font-semibold text-ink">Venue pipeline</p>
              <p class="mt-2 text-xs text-muted">Monitor confirmations and follow-ups.</p>
            </div>
            <div class="rounded-2xl border border-orange-100 bg-white p-4">
              <p class="text-sm font-semibold text-ink">Package tracker</p>
              <p class="mt-2 text-xs text-muted">Stay on top of quotes and revisions.</p>
            </div>
            <div class="rounded-2xl border border-orange-100 bg-white p-4">
              <p class="text-sm font-semibold text-ink">Quick approvals</p>
              <p class="mt-2 text-xs text-muted">Send clients a polished summary.</p>
            </div>
            <div class="rounded-2xl border border-orange-100 bg-[#FFF3E5] p-4">
              <p class="text-sm font-semibold text-ink">Insightful notes</p>
              <p class="mt-2 text-xs text-muted">Capture preferences on the fly.</p>
            </div>
          </div>
        </section>

        <section class="rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
          <h2 class="text-xl font-semibold text-ink">Log in</h2>
          <p class="mt-2 text-sm text-muted">Use your vendor credentials to continue.</p>

          <form class="mt-6 space-y-4" @submit.prevent="handleSubmit">
            <p v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
              {{ error }}
            </p>
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide text-muted">Username</span>
              <input
                type="text"
                required
                v-model="username"
                placeholder="your.username"
                class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
              >
            </label>
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide text-muted">Password</span>
              <div class="relative">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  required
                  v-model="password"
                  placeholder="Enter your password"
                  class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
                >
                <button
                  type="button"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                  @click="showPassword = !showPassword"
                  aria-label="Toggle password visibility"
                >
                  <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </label>

            <div class="flex items-center justify-between text-xs text-muted">
              <label class="inline-flex items-center gap-2">
                <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand">
                <span>Keep me signed in</span>
              </label>
              <RouterLink class="font-semibold text-brand" to="/forgot-password">Forgot password?</RouterLink>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
            >
              {{ loading ? 'Signing in...' : 'Continue to dashboard' }}
            </button>
          </form>

          <div class="mt-6 text-center text-xs text-muted">
            New to Invi8?
            <RouterLink class="font-semibold text-brand" to="/register">Create an account</RouterLink>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const loading = ref(false)
const showPassword = ref(false)
const error = ref('')

const handleSubmit = async () => {
  error.value = ''
  loading.value = true
  const formData = new FormData()
  formData.append('s_username', username.value)
  formData.append('s_password', password.value)

  try {
    const { data } = await api.loginVendor(formData)
    const responseItems = Array.isArray(data?.data) ? data.data : []
    const primaryUser = responseItems[0] || null
    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const user =
      data?.user ||
      primaryUser ||
      data?.data?.user ||
      data?.data?.profile ||
      data?.profile ||
      (data?.data && typeof data.data === 'object' ? data.data : null)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    const resolvedUserId =
      user?.s_id ||
      user?.id ||
      user?.user_id ||
      user?.vendor_id ||
      user?.vendorId ||
      ''

    if (isError) {
      throw new Error(data?.msg || data?.message || 'Login failed')
    }

    if (token) {
      setAuthToken(token)
    }

    if (user) {
      setAuthUser({
        ...user,
        s_id: resolvedUserId || user?.s_id || ''
      })
    } else if (username.value) {
      setAuthUser({
        name: username.value,
        username: username.value,
        s_id: resolvedUserId
      })
    }

    setAuthSession({
      loggedIn: true,
      s_id: resolvedUserId
    })

    const redirectPath = route.query.redirect || '/dashboard'
    router.push(redirectPath)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Login failed'
  } finally {
    loading.value = false
  }
}
</script>
